<template>
  <b-container fluid>
    <h2 class="my-3 font-weight-bold">{{ Title }}</h2>
    <h5 >{{ Subtitle }}</h5>
    <b-row class="mb-4">
      <b-col 
        v-for="(step, index) in Steps" 
        :key="'nav-' + index"
        class="text-center step-col px-3"
        :class="{ 'completed-line': index < currentStep }">
        <div class="d-flex flex-column align-items-center position-relative" style="z-index: 2;">
          <div class="mb-2 d-flex align-items-center justify-content-center">
            <b-icon v-if="index < currentStep" 
              icon="check-circle-fill" 
              style="color: #20c997;" 
              font-scale="1.8"/>
            <b-icon 
              v-else-if="currentStep === index" 
              icon="record-circle" 
              :variant="Variant" 
              font-scale="1.8"/>
            <b-icon 
              v-else 
              icon="circle" 
              style="color: #dee2e6;" 
              font-scale="1.8"/>
          </div>
          <div class="text-center" style="line-height: 1.2;">
            <div class="font-weight-bold" 
                 :class="currentStep === index ? 'text-' + Variant : 'text-dark'" 
                 style="font-size: 0.85rem;">
              {{ step.title }}
            </div>
            <small class="text-muted" style="font-size: 0.70rem;">
              {{ step.description || '' }}
            </small>
          </div>
        </div>
      </b-col>
    </b-row>
    <div v-for="(step, index) in Steps" :key="index">
      <b-card v-show="currentStep === index" class="mb-4">
        <ValidationObserver :ref="'stepObserver-' + index" v-slot="{ valid: stepValid }">
          <b-row class="d-flex justify-content-center">
            <b-col cols="8">
              <hr>
                <slot :name="'step-' + (index + 1)"></slot>
              <hr>
            </b-col>
            <b-col cols="8">
              <div class="d-flex justify-content-end">
                <b-button @click="cancel" pill :variant="'outline-' + Variant" class="mr-1">
                  {{$t('Cancel')}}
                </b-button>
                <b-button v-if="currentStep > 0" @click="prevStep" pill :variant="'outline-' + Variant" class="mr-1">
                  {{$t('Previous')}}
                </b-button>
                <b-button v-if="currentStep < Steps.length - 1" @click="nextStep" pill :disabled="!(stepValid || Valid)" :variant="Variant">
                  {{$t('Next')}}
                </b-button>
                <b-button v-else @click="submit" pill :disabled="!(stepValid || Valid)" :variant="Variant">
                  {{$t('Finish')}}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </ValidationObserver>
      </b-card>
    </div>
  </b-container>
</template>
<script lang="ts">

  import { Component, InjectReactive, Prop, PropSync, Vue } from 'vue-property-decorator';
  import { ValidationObserver } from 'vee-validate';

  /**
   * Traductions
   */
  import locale from './locales/Stepper.json';

	@Component({
    components: { ValidationObserver },
    i18n: {messages: locale}
  })
  /**
   * Composant globale pour la mise en place de formulaire par étapes
   */
	export default class GenericStepper extends Vue {

        /**
       * Titre formulaire
       */
        @Prop( {default: () => "" } )
        private Title!: string;

        /**
         * Sous titre formulaire
         */
        @Prop( {default: () => "" } )
        private Subtitle!: string;

        /**
        * Formulaire valide
        */
        @Prop( {default: () => false } )
        private Valid!: boolean;

        /**
         * Etapes du formulaire
         */
        @Prop( {default: () => [] } )
        private Steps!: { title: string, description: string }[];

        /**
         * Etape courante
         */
        @PropSync('CurrentStep', { default: 0 })
        private currentStep!: number;

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * Annulation du formulaire
         */
        public cancel(){
          this.$emit('cancel');
        }

        /**
         * Submit
         */
        public submit(){
          this.$emit('submit');
        }

        /**
         * Passe à l'étape suivante
         */
        public nextStep() {
            if (this.currentStep < this.Steps.length - 1) {
                this.currentStep++;
                this.$emit('step-forward', this.currentStep);
            }
        }

        /**
         * Passe à l'étape précédente
         */
        public prevStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
                this.$emit('step-backward', this.currentStep);
            }
        }
    }

</script>
<style lang="css" scoped>
.step-col {
  position: relative;
}

/* Création du trait de base (gris) */
.step-col:not(:last-child)::after {
  content: '';
  position: absolute;
  /* Centrage exactement au milieu de toute la colonne (icône + texte) */
  top: 50%;
  transform: translateY(-50%);
  
  /* L'astuce pour le fond transparent : on décale le départ de la ligne
     et on réduit sa largeur pour créer un "trou" au milieu */
  left: calc(50% + 60px);     /* Démarre 60px après le centre exact */
  width: calc(100% - 120px);  /* Prend la largeur restante jusqu'au bloc suivant */
  
  height: 2px;
  background-color: #e9ecef;
  z-index: 1;
}

/* Trait complété (vert) */
.step-col.completed-line:not(:last-child)::after {
  background-color: #20c997; 
}
</style>